import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { lintGutter } from '@codemirror/lint';
import {
  ScaffolderFormPaper,
  ScaffolderFormHeader,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import { githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import React, { useCallback, useEffect, useRef } from 'react';

import { Box } from '@material-ui/core';

import { TEMPLATE_PATCH_INFO } from '../infoDoc';
import { useStyles, useGeneratorsStyles } from '../styles';
import { templatePatchInputErrors } from '../validation';

interface ApplicationSetTemplatePatchSectionProps {
  value: string;
  onChange: (value: string) => void;
  errors: string[];
}

const ApplicationSetTemplatePatchSectionInner = ({
  value,
  onChange,
  errors,
}: ApplicationSetTemplatePatchSectionProps) => {
  const classes = useStyles();
  const generatorClasses = useGeneratorsStyles();
  const templatePatchError =
    errors.length > 0 ? templatePatchInputErrors(value) : '';
  const sectionRef = useRef<HTMLDivElement>(null);

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleChangeTemplatePatch = useCallback((v: string) => {
    onChangeRef.current(v);
  }, []);

  useEffect(() => {
    if (templatePatchError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [templatePatchError]);

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Template Patch"
          description="Provide a YAML patch to modify the AppSet template used for deploying applications."
          divider
          infoDoc={TEMPLATE_PATCH_INFO}
        />
        <Box margin={1} marginBottom={2}>
          <Box className={generatorClasses.codeMirror}>
            <CodeMirror
              value={value || ''}
              minHeight="300px"
              extensions={[
                typeof yamlLang === 'function' ? yamlLang() : ([] as any),
                lintGutter(),
              ]}
              onChange={handleChangeTemplatePatch}
              theme={githubLight}
              placeholder="Enter Template Patch YAML"
            />
          </Box>
          {templatePatchError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={templatePatchError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const ApplicationSetTemplatePatchSection = React.memo(
  ApplicationSetTemplatePatchSectionInner,
);
